import { ref, nextTick } from 'vue';

// Global state for the chat widget
const isReady = ref(false);
const isInitialized = ref(false);
const retryCount = ref(0);
const maxRetries = 3;
const isUsingPopup = ref(false); // Track if popup is being used

export function useChatWidget() {
  const isError = ref(false);
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Initialize Chatwoot widget
   */
  const initializeChatwoot = async () => {
    if (typeof window === 'undefined') {
      console.warn('🟡 useChatWidget: Window not available, cannot initialize');
      return;
    }

    // If Chatwoot is already loaded, mark as ready
    if (window.$chatwoot && typeof window.$chatwoot.toggle === 'function') {
      isReady.value = true;
      return;
    }

    try {
      // Set up the configuration before loading
      window.chatwootSettings = {
        hideMessageBubble: true,
        position: 'right',
        locale: 'en',
        useBrowserLanguage: false
      };

      // Wait for Chatwoot to be ready
      const checkChatwootReady = () => {
        return new Promise((resolve, reject) => {
          const maxAttempts = 50; // 10 seconds max
          let attempts = 0;

          const checkInterval = setInterval(() => {
            attempts++;
            
            if (window.$chatwoot && typeof window.$chatwoot.toggle === 'function') {
              clearInterval(checkInterval);
              resolve();
            } else if (attempts >= maxAttempts) {
              clearInterval(checkInterval);
              reject(new Error('Chatwoot initialization timeout'));
            }
          }, 200);
        });
      };

      await checkChatwootReady();
      
      isReady.value = true;
      isError.value = false;

      // Emit ready event
      window.dispatchEvent(new CustomEvent('chatwoot:ready'));

    } catch (error) {
      console.error('🔴 useChatWidget: Failed to initialize Chatwoot:', error);
      console.error('🔴 useChatWidget: Error details:', {
        message: error.message,
        stack: error.stack,
        chatwootExists: typeof window.$chatwoot !== 'undefined',
        chatwootSDKExists: typeof window.chatwootSDK !== 'undefined'
      });
      
      isError.value = true;
      isReady.value = false;
      throw error;
    }
  };

  /**
   * Open Chatwoot widget
   */
  const openChatwoot = async () => {
    if (!isReady.value) {
      try {
        await initializeChatwoot();
      } catch (error) {
        isUsingPopup.value = true;
        openChatwootPopup();
        return;
      }
    }

    if (!window.$chatwoot) {
      isUsingPopup.value = true;
      openChatwootPopup();
      return;
    }

    try {
      window.$chatwoot.toggle();

      // Reset popup flag since we're trying embedded widget
      isUsingPopup.value = false;

      // Check if embedded widget opened successfully
      setTimeout(() => {
        const chatwootFrame = document.querySelector('iframe[src*="insuranceexpresschat.com"]');
        const chatwootWidget = document.querySelector('.woot-widget-expanded, .woot--widget-expanded, [data-widget-type="chatwoot"]');
        
        // If frame exists but widget is not visible (X-Frame-Options blocking), try popup fallback
        if (chatwootFrame && !chatwootWidget) {
          isUsingPopup.value = true;
          openChatwootPopup();
        } else if (!chatwootFrame && !chatwootWidget) {
          isUsingPopup.value = true;
          openChatwootPopup();
        } else {
          // Dispatch success event for embedded widget
          window.dispatchEvent(new CustomEvent('chatwoot:widget-opened', {
            detail: { source: 'embedded-widget', timestamp: Date.now() }
          }));
        }
      }, 1000);

    } catch (error) {
      console.error('🔴 useChatWidget: Error calling $chatwoot.toggle():', error);
      isUsingPopup.value = true;
      openChatwootPopup();
    }
  };

  /**
   * Open Chatwoot in a popup window as fallback
   */
  const openChatwootPopup = () => {
    try {
      // Use the correct working Chatwoot widget URL with HTTPS
      const chatUrl = 'https://insuranceexpresschat.com/widget?website_token=5MUh9hiKzyW9fE3MtxJHnzh9';
      
      const popup = window.open(
        chatUrl,
        'chatwoot-chat',
        'width=400,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
      );
      
      if (popup) {
        // Focus the popup
        popup.focus();
        
        // Check if popup was closed quickly (blocked)
        setTimeout(() => {
          if (popup.closed) {
            // Show user-friendly message for blocked popups
            showPopupBlockedMessage();
          }
        }, 1000);
        
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('chatwoot:popup-opened', {
          detail: { source: 'custom-widget-popup', timestamp: Date.now() }
        }));
      } else {
        console.error('🔴 useChatWidget: Failed to open popup - popup blocked');
        showPopupBlockedMessage();
      }
    } catch (error) {
      console.error('🔴 useChatWidget: Error opening popup:', error);
      showErrorMessage();
    }
  };

  /**
   * Show user-friendly message when popup is blocked
   */
  const showPopupBlockedMessage = () => {
    // Create a better user experience than alert()
    const message = `
      Chat window was blocked by your browser's popup blocker.
      
      To chat with us:
      1. Allow popups for this site, or
      2. Contact us directly at: support@insuranceexpress.com
      3. Call us at: (your phone number)
    `;
    
    // Try to use a more elegant notification if available
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        title: 'Chat Blocked',
        message: 'Please allow popups to open chat, or contact us directly.',
        type: 'warning',
        actions: [
          { label: 'Contact Us', action: () => window.location.href = 'mailto:support@insuranceexpress.com' }
        ]
      });
    } else {
      // Fallback to alert if no notification system available
      alert(message);
    }
  };

  /**
   * Show user-friendly error message
   */
  const showErrorMessage = () => {
    const message = `
      Unable to open chat at this time.
      
      Please contact us directly:
      Email: support@insuranceexpress.com
      Phone: (your phone number)
    `;
    
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        title: 'Chat Unavailable',
        message: 'Please contact us directly via email or phone.',
        type: 'error',
        actions: [
          { label: 'Email Us', action: () => window.location.href = 'mailto:support@insuranceexpress.com' }
        ]
      });
    } else {
      alert(message);
    }
  };

  /**
   * Close Chatwoot widget
   */
  const closeChatwoot = () => {
    if (window.$chatwoot && typeof window.$chatwoot.toggle === 'function') {
      try {
        window.$chatwoot.toggle();

        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('chatwoot:widget-closed', {
          detail: { source: 'custom-widget', timestamp: Date.now() }
        }));

      } catch (error) {
        console.error('🔴 useChatWidget: Error closing Chatwoot:', error);
      }
    } else {
      console.warn('🟡 useChatWidget: Cannot close - Chatwoot not available');
    }
  };

  /**
   * Toggle Chatwoot widget
   */
  const toggleChatwoot = async () => {
    try {
      if (window.$chatwoot) {
        // Check current state if possible
        const isCurrentlyOpen = checkIfOpen();
        
        if (isCurrentlyOpen) {
          closeChatwoot();
        } else {
          await openChatwoot();
        }
      } else {
        await openChatwoot();
      }
    } catch (err) {
      console.error('Failed to toggle Chatwoot:', err);
      throw err;
    }
  };

  /**
   * Hide Chatwoot bubble
   */
  const hideChatwoot = () => {
    try {
      if (window.$chatwoot) {
        window.$chatwoot.toggleBubbleVisibility(false);
      }
    } catch (err) {
      console.error('Failed to hide Chatwoot:', err);
    }
  };

  /**
   * Show Chatwoot bubble
   */
  const showChatwoot = () => {
    try {
      if (window.$chatwoot) {
        window.$chatwoot.toggleBubbleVisibility(true);
      }
    } catch (err) {
      console.error('Failed to show Chatwoot:', err);
    }
  };

  /**
   * Set user information
   */
  const setUser = (userInfo) => {
    try {
      if (window.$chatwoot && userInfo) {
        window.$chatwoot.setUser(userInfo.identifier, {
          name: userInfo.name,
          email: userInfo.email,
          phone: userInfo.phone,
          ...userInfo.customAttributes
        });
      }
    } catch (err) {
      console.error('Failed to set user info:', err);
    }
  };

  /**
   * Reset user session
   */
  const resetUser = () => {
    try {
      if (window.$chatwoot) {
        window.$chatwoot.reset();
      }
    } catch (err) {
      console.error('Failed to reset user:', err);
    }
  };

  /**
   * Check if chat widget is currently open (best effort)
   */
  const checkIfOpen = () => {
    try {
      // This is a best-effort check as Chatwoot doesn't expose widget state directly
      const chatWidget = document.querySelector('.woot-widget-holder');
      if (chatWidget) {
        const bubble = chatWidget.querySelector('.woot-widget-bubble');
        const expanded = chatWidget.querySelector('.woot-widget-expanded');
        return expanded && expanded.style.display !== 'none';
      }
      return false;
    } catch (err) {
      console.error('Failed to check widget state:', err);
      return false;
    }
  };

  /**
   * Send a message programmatically
   */
  const sendMessage = (message, attachments = []) => {
    try {
      if (window.$chatwoot && message) {
        // Note: This depends on Chatwoot's API which may vary
        window.dispatchEvent(new CustomEvent('chatwoot:send-message', {
          detail: { message, attachments }
        }));
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  /**
   * Get unread message count (if available)
   */
  const getUnreadCount = () => {
    try {
      // This would require custom implementation or Chatwoot API
      // For now, return 0 as placeholder
      return 0;
    } catch (err) {
      console.error('Failed to get unread count:', err);
      return 0;
    }
  };

  /**
   * Set custom attributes
   */
  const setCustomAttributes = (attributes) => {
    try {
      if (window.$chatwoot && attributes) {
        window.$chatwoot.setCustomAttributes(attributes);
      }
    } catch (err) {
      console.error('Failed to set custom attributes:', err);
    }
  };

  /**
   * Set locale for internationalization
   */
  const setLocale = (locale) => {
    try {
      if (window.$chatwoot && locale) {
        window.$chatwoot.setLocale(locale);
      }
    } catch (err) {
      console.error('Failed to set locale:', err);
    }
  };

  return {
    // State
    isReady,
    isError,
    isLoading,
    error,
    isUsingPopup,
    
    // Methods
    initializeChatwoot,
    openChatwoot,
    openChatwootPopup,
    closeChatwoot,
    toggleChatwoot,
    hideChatwoot,
    showChatwoot,
    setUser,
    resetUser,
    checkIfOpen,
    sendMessage,
    getUnreadCount,
    setCustomAttributes,
    setLocale,
    showPopupBlockedMessage,
    showErrorMessage
  };
} 